/**
 * Escena de LA CASA (headquarter del grupo, canon Adri 2026-07-11, ver
 * `docs/biblia/escenarios.md`). El TERRENO de la caseta, no el interior.
 *
 * Pinta por código la caseta rústica en un claro del monte al atardecer
 * (rects cálidos, sol bajo) y pone a la vista los dos elementos del terreno
 * canon: la **barra de dominadas** y la **canasta de basket** — cada uno
 * lanza su minijuego. Ofrece también dos accesos: bajar al campo de fútbol
 * y entrar dentro de la casa.
 *
 * Separación dura: todo texto viene de `src/data/zonas-actividad.ts`; esta
 * escena solo dibuja y enruta.
 *
 * Contrato de escena (igual que el resto de escenas del proyecto):
 *  - `KEY` público para que el orquestador lo registre en nav.ts.
 *  - HUD visible arriba (ensureRunning + bringToTop).
 *  - Sin assets binarios: todo es Phaser.Graphics + Text.
 *  - Botones con el mismo patrón (graphics + zone interactiva) que
 *    InteriorCasaScene / PlaceholderScene.
 */

import Phaser from 'phaser';
import {
  ensureRunning,
  goTo,
  SCENE_KEYS,
  type SceneKey,
} from '@/engine/nav';
import {
  BOTON_BAJAR_CAMPO,
  BOTON_ENTRAR_DENTRO,
  BOTON_VOLVER_BARRIO,
  DESCRIPCION_LA_CASA,
} from '@/data/zonas-actividad';
import { getMinigame } from '@/data/minijuegos';
import { CampoFutbolScene } from './CampoFutbolScene';

// Paleta coherente con InteriorCasaScene / HubScene / MissionScene.
const COLOR_FONDO_CIELO = 0xf6c065; // naranja dorado del atardecer
const COLOR_CIELO_ALTO = 0xff9a3c; // banda más alta, naranja más vivo
const COLOR_SUELO = 0x7a5a32; // tierra del claro
const COLOR_HIERBA = 0x9aa84a; // verde seco de la hierba
const COLOR_CASETA_PARED = 0xc97b4a; // pared cálida de la caseta
const COLOR_CASETA_TECHO = 0x5a3a26; // tejado marrón oscuro
const COLOR_CASETA_PUERTA = 0x3b2412; // puerta marrón oscuro
const COLOR_CASETA_VENTANA = 0xffd070; // ventana iluminada (luz cálida dentro)
const COLOR_SOL = 0xffe07a; // disco del sol bajo
const COLOR_PINO = 0x2e5a32; // verde pino oscuro
const COLOR_PINO_TRONCO = 0x3b2412;
const COLOR_BARRA_METAL = 0x444452; // barra de dominadas
const COLOR_CANASTA_TABLERO = 0xffffff;
const COLOR_CANASTO_ARO = 0xff7f24;
const COLOR_PANEL = 0x22222e;
const COLOR_BOTON = 0x2a2a38;
const COLOR_BOTON_HOVER = 0x38384a;
const COLOR_BOTON_DESTACADO = 0xc0392b; // rojo para los minijuegos
const COLOR_BOTON_DESTACADO_HOVER = 0xe74c3c;
const COLOR_BORDE_PANEL = 0x38384a;
const COLOR_TEXTO = '#ffffff';
const COLOR_TEXTO_DIM = '#8a8a95';
const COLOR_TEXTO_DESTACADO = '#fff8e1';

const HUD_ALTO = 100;
const MARGEN = 24;

export class LaCasaScene extends Phaser.Scene {
  /** Key pública — el orquestador la registra en nav.ts. */
  public static readonly KEY = 'LaCasaScene';

  /** Contenedor del ambiente (fondo, caseta, sol, cancha): se dibuja una
   *  vez y NO se limpia al re-renderizar el UI. */
  private ambienteContainer!: Phaser.GameObjects.Container;
  /** Contenedor del UI re-renderizable (panel + botones). */
  private uiContainer!: Phaser.GameObjects.Container;

  constructor() {
    super(LaCasaScene.KEY);
  }

  create(): void {
    const { width, height } = this.scale;
    this.cameras.main.setBackgroundColor(COLOR_FONDO_CIELO);

    ensureRunning(this, SCENE_KEYS.HUD);
    this.scene.bringToTop(SCENE_KEYS.HUD);

    this.ambienteContainer = this.add.container(0, 0);
    this.uiContainer = this.add.container(0, 0);

    this.dibujarAmbiente(width, height);
    this.renderUI(width, height);
  }

  // ---------------------------------------------------------------------
  // Ambiente: caseta rústica al atardecer con barra y cancha a la vista
  // ---------------------------------------------------------------------

  private dibujarAmbiente(width: number, height: number): void {
    // Cielo en dos bandas (la baja más cálida, imitando atardecer).
    const cielo = this.add.graphics();
    cielo.fillStyle(COLOR_CIELO_ALTO, 1);
    cielo.fillRect(0, 0, width, height * 0.55);
    cielo.fillStyle(COLOR_FONDO_CIELO, 1);
    cielo.fillRect(0, height * 0.35, width, height * 0.35);
    this.ambienteContainer.add(cielo);

    // Disco solar bajo el horizonte (a la izquierda).
    const sol = this.add.graphics();
    sol.fillStyle(COLOR_SOL, 1);
    sol.fillCircle(width * 0.18, height * 0.42, 56);
    sol.fillStyle(0xfff3b0, 0.7);
    sol.fillCircle(width * 0.18, height * 0.42, 36);
    this.ambienteContainer.add(sol);

    // Halo cálido alrededor del sol.
    const halo = this.add.graphics();
    halo.fillStyle(0xffe07a, 0.18);
    halo.fillCircle(width * 0.18, height * 0.42, 180);
    this.ambienteContainer.add(halo);

    // Línea de pinos al fondo (silueta).
    const pinosFondo = this.add.graphics();
    pinosFondo.fillStyle(COLOR_PINO, 1);
    for (let i = 0; i < 12; i++) {
      const px = (width / 12) * i + 10;
      const py = height * 0.36 + (i % 2 === 0 ? 0 : 8);
      pinosFondo.fillTriangle(px, py, px + 16, py + 70, px + 32, py);
    }
    this.ambienteContainer.add(pinosFondo);

    // Suelo del claro (tierra + hierba seca).
    const suelo = this.add.graphics();
    suelo.fillStyle(COLOR_SUELO, 1);
    suelo.fillRect(0, height * 0.55, width, height * 0.45);
    this.ambienteContainer.add(suelo);

    const cesped = this.add.graphics();
    cesped.fillStyle(COLOR_HIERBA, 0.5);
    cesped.fillRect(0, height * 0.62, width, 12);
    this.ambienteContainer.add(cesped);

    // Pino aislado a la derecha de la caseta (decoración lateral).
    const pino = this.add.graphics();
    pino.fillStyle(COLOR_PINO_TRONCO, 1);
    pino.fillRect(width * 0.78, height * 0.42, 8, 80);
    pino.fillStyle(COLOR_PINO, 1);
    pino.fillTriangle(
      width * 0.78 - 18,
      height * 0.5,
      width * 0.78 + 34,
      height * 0.5,
      width * 0.78 + 8,
      height * 0.36,
    );
    pino.fillTriangle(
      width * 0.78 - 12,
      height * 0.45,
      width * 0.78 + 28,
      height * 0.45,
      width * 0.78 + 8,
      height * 0.32,
    );
    this.ambienteContainer.add(pino);

    // Caseta rústica en el centro del claro.
    this.dibujarCaseta(width * 0.5, height * 0.46, 220, 110);

    // Barra de dominadas a la izquierda de la caseta (recordatorio del
    // minijuego mg-dominadas).
    this.dibujarBarraDominadas(width * 0.22, height * 0.6);

    // Canasta de basket a la derecha de la caseta (recordatorio del
    // minijuego mg-21-canasta).
    this.dibujarCanasta(width * 0.78 - 80, height * 0.58);
  }

  private dibujarCaseta(cx: number, cy: number, ancho: number, alto: number): void {
    // Cuerpo de la caseta (rectángulo cálido).
    const cuerpo = this.add.graphics();
    cuerpo.fillStyle(COLOR_CASETA_PARED, 1);
    cuerpo.fillRect(cx - ancho / 2, cy, ancho, alto);
    cuerpo.lineStyle(2, COLOR_CASETA_TECHO, 1);
    cuerpo.strokeRect(cx - ancho / 2, cy, ancho, alto);
    this.ambienteContainer.add(cuerpo);

    // Tejado triangular encima.
    const tejado = this.add.graphics();
    tejado.fillStyle(COLOR_CASETA_TECHO, 1);
    tejado.fillTriangle(
      cx - ancho / 2 - 14,
      cy,
      cx + ancho / 2 + 14,
      cy,
      cx,
      cy - 50,
    );
    this.ambienteContainer.add(tejado);

    // Puerta marrón oscuro centrada.
    const puerta = this.add.graphics();
    puerta.fillStyle(COLOR_CASETA_PUERTA, 1);
    puerta.fillRect(cx - 18, cy + alto - 56, 36, 56);
    this.ambienteContainer.add(puerta);

    // Ventana iluminada a la derecha (sugiere que hay alguien dentro).
    const ventana = this.add.graphics();
    ventana.fillStyle(COLOR_CASETA_VENTANA, 1);
    ventana.fillRect(cx + ancho / 2 - 50, cy + 22, 28, 28);
    ventana.lineStyle(2, COLOR_CASETA_TECHO, 1);
    ventana.strokeRect(cx + ancho / 2 - 50, cy + 22, 28, 28);
    this.ambienteContainer.add(ventana);

    // Travesaño de la ventana.
    const traves = this.add.graphics();
    traves.lineStyle(2, COLOR_CASETA_TECHO, 1);
    traves.beginPath();
    traves.moveTo(cx + ancho / 2 - 36, cy + 36);
    traves.lineTo(cx + ancho / 2 - 36, cy + 50);
    traves.strokePath();
    this.ambienteContainer.add(traves);

    // Humo saliendo de un "tubo" lateral (toque de barbacoa).
    const tubo = this.add.graphics();
    tubo.fillStyle(0x444452, 1);
    tubo.fillRect(cx - ancho / 2 + 12, cy + 10, 8, 18);
    this.ambienteContainer.add(tubo);

    const humo = this.add.graphics();
    humo.fillStyle(0xffffff, 0.35);
    humo.fillCircle(cx - ancho / 2 + 16, cy - 4, 10);
    humo.fillStyle(0xffffff, 0.2);
    humo.fillCircle(cx - ancho / 2 + 22, cy - 16, 14);
    this.ambienteContainer.add(humo);
  }

  private dibujarBarraDominadas(cx: number, cy: number): void {
    // Dos postes metálicos verticales y una barra horizontal arriba.
    const postes = this.add.graphics();
    postes.fillStyle(COLOR_BARRA_METAL, 1);
    postes.fillRect(cx - 22, cy, 6, 80);
    postes.fillRect(cx + 16, cy, 6, 80);
    this.ambienteContainer.add(postes);

    const barra = this.add.graphics();
    barra.fillStyle(COLOR_BARRA_METAL, 1);
    barra.fillRect(cx - 28, cy - 8, 50, 6);
    barra.lineStyle(2, 0x222230, 1);
    barra.strokeRect(cx - 28, cy - 8, 50, 6);
    this.ambienteContainer.add(barra);

    // Etiqueta "DOMINADAS" pequeña debajo (no interactiva, decorativa).
    this.ambienteContainer.add(
      this.add
        .text(cx - 2, cy + 100, '💪', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '24px',
        })
        .setOrigin(0.5),
    );
  }

  private dibujarCanasta(cx: number, cy: number): void {
    // Poste vertical.
    const poste = this.add.graphics();
    poste.fillStyle(COLOR_BARRA_METAL, 1);
    poste.fillRect(cx + 40, cy - 30, 4, 110);
    this.ambienteContainer.add(poste);

    // Tablero blanco.
    const tablero = this.add.graphics();
    tablero.fillStyle(COLOR_CANASTA_TABLERO, 1);
    tablero.fillRect(cx - 30, cy - 30, 70, 50);
    tablero.lineStyle(2, COLOR_BORDE_PANEL, 1);
    tablero.strokeRect(cx - 30, cy - 30, 70, 50);
    this.ambienteContainer.add(tablero);

    // Cuadradito del tablero debajo del aro.
    tablero.lineStyle(2, 0x222230, 0.6);
    tablero.strokeRect(cx + 5, cy - 5, 30, 20);

    // Aro naranja.
    this.ambienteContainer.add(
      this.add.circle(cx + 20, cy + 18, 12, COLOR_CANASTO_ARO).setStrokeStyle(3, 0xffffff, 1),
    );

    // Emoji basket como remate (no interactivo).
    this.ambienteContainer.add(
      this.add
        .text(cx + 60, cy + 70, '🏀', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '22px',
        })
        .setOrigin(0.5),
    );
  }

  // ---------------------------------------------------------------------
  // UI re-renderizable: panel ambient + 4 botones + volver
  // ---------------------------------------------------------------------

  private renderUI(width: number, height: number): void {
    this.limpiarUI();

    this.renderPanelAmbient(width);
    this.renderBotonera(width, height);
    this.renderBotonVolver(width, height);
  }

  private limpiarUI(): void {
    if (!this.uiContainer) return;
    this.uiContainer.removeAll(true);
  }

  private renderPanelAmbient(width: number): void {
    // Panel pequeño arriba con nombre + ambient — debajo del HUD.
    const panelAncho = width - MARGEN * 2;
    const panelAlto = 120;
    const y = HUD_ALTO + 70;

    const panel = this.add.graphics();
    panel.fillStyle(COLOR_PANEL, 0.92);
    panel.fillRoundedRect(-panelAncho / 2, -panelAlto / 2, panelAncho, panelAlto, 18);
    panel.lineStyle(2, COLOR_BORDE_PANEL, 1);
    panel.strokeRoundedRect(-panelAncho / 2, -panelAlto / 2, panelAncho, panelAlto, 18);
    panel.setPosition(width / 2, y);
    this.uiContainer.add(panel);

    // Emoji + nombre de la zona.
    this.uiContainer.add(
      this.add
        .text(width / 2 - panelAncho / 2 + 50, y - 18, DESCRIPCION_LA_CASA.emoji, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '34px',
        })
        .setOrigin(0.5),
    );
    this.uiContainer.add(
      this.add.text(width / 2 - panelAncho / 2 + 96, y - 22, DESCRIPCION_LA_CASA.nombre, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '20px',
        fontStyle: 'bold',
        color: COLOR_TEXTO_DESTACADO,
      }),
    );

    // Ambient debajo (tamaño y ancho de caja ajustados para que NO desborde
    // el panel por la derecha en 720 de ancho).
    this.uiContainer.add(
      this.add.text(width / 2 - 8, y + 16, DESCRIPCION_LA_CASA.ambient, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '13px',
        color: COLOR_TEXTO,
        align: 'left',
        wordWrap: { width: panelAncho - 150 },
      }),
    );
  }

  private renderBotonera(width: number, height: number): void {
    // 4 botones en bloque (2 filas x 2 cols), debajo del panel ambient.
    const areaTop = HUD_ALTO + 220;
    const areaBottom = height - 110;
    const gap = 12;
    const columnas = 2;
    const filas = 2;
    const anchoTotal = width - MARGEN * 2;
    const altoTotal = areaBottom - areaTop;
    const botonAlto = (altoTotal - gap * (filas - 1)) / filas;
    const botonAncho = (anchoTotal - gap * (columnas - 1)) / columnas;

    type BtnSpec = {
      emoji: string;
      titulo: string;
      subtitulo: string;
      destacado: boolean;
      alPulsar: () => void;
    };

    const mgCanasta = getMinigame('mg-21-canasta');
    const mgDominadas = getMinigame('mg-dominadas');

    const botones: BtnSpec[] = [
      {
        emoji: '🏀',
        titulo: 'El 21 en la canasta',
        subtitulo: mgCanasta.mecanica,
        destacado: true,
        alPulsar: () =>
          goTo(this, SCENE_KEYS.MG_CANASTA, { volverA: LaCasaScene.KEY }),
      },
      {
        emoji: '💪',
        titulo: 'Las 100 dominadas',
        subtitulo: mgDominadas.mecanica,
        destacado: true,
        alPulsar: () =>
          goTo(this, SCENE_KEYS.MG_DOMINADAS, { volverA: LaCasaScene.KEY }),
      },
      {
        emoji: '⚽',
        titulo: BOTON_BAJAR_CAMPO,
        subtitulo: 'La pista de tierra con el tronco de red.',
        destacado: false,
        alPulsar: () => goTo(this, CampoFutbolScene.KEY as SceneKey),
      },
      {
        emoji: '🛋️',
        titulo: BOTON_ENTRAR_DENTRO,
        subtitulo: 'La mesa grande, el fuego y la estufa.',
        destacado: false,
        alPulsar: () => goTo(this, SCENE_KEYS.INTERIOR),
      },
    ];

    botones.forEach((b, i) => {
      const fila = Math.floor(i / columnas);
      const col = i % columnas;
      const x = MARGEN + botonAncho / 2 + col * (botonAncho + gap);
      const y = areaTop + fila * (botonAlto + gap) + botonAlto / 2;
      this.crearBoton(b, x, y, botonAncho, botonAlto);
    });
  }

  private crearBoton(
    spec: {
      emoji: string;
      titulo: string;
      subtitulo: string;
      destacado: boolean;
      alPulsar: () => void;
    },
    x: number,
    y: number,
    ancho: number,
    alto: number,
  ): void {
    const colorBoton = spec.destacado ? COLOR_BOTON_DESTACADO : COLOR_BOTON;
    const colorHover = spec.destacado ? COLOR_BOTON_DESTACADO_HOVER : COLOR_BOTON_HOVER;

    const boton = this.add.graphics();
    const dibujar = (color: number) => {
      boton.clear();
      boton.fillStyle(color, 1);
      boton.fillRoundedRect(-ancho / 2, -alto / 2, ancho, alto, 14);
    };
    dibujar(colorBoton);
    boton.setPosition(x, y);
    this.uiContainer.add(boton);

    // Emoji grande a la izquierda.
    this.uiContainer.add(
      this.add
        .text(x - ancho / 2 + 28, y - 8, spec.emoji, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '30px',
        })
        .setOrigin(0, 0.5),
    );

    // Título.
    this.uiContainer.add(
      this.add.text(x - ancho / 2 + 70, y - 10, spec.titulo, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '18px',
        fontStyle: 'bold',
        color: COLOR_TEXTO,
        wordWrap: { width: ancho - 90 },
      }),
    );

    // Subtítulo (descripción / mecánica).
    this.uiContainer.add(
      this.add.text(x - ancho / 2 + 70, y + 14, spec.subtitulo, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '13px',
        color: COLOR_TEXTO_DIM,
        wordWrap: { width: ancho - 90 },
      }),
    );

    const zona = this.add.zone(x, y, ancho, alto).setInteractive({ useHandCursor: true });
    zona.on('pointerover', () => dibujar(colorHover));
    zona.on('pointerout', () => dibujar(colorBoton));
    zona.on('pointerdown', () => dibujar(colorHover));
    zona.on('pointerup', () => spec.alPulsar());
    this.uiContainer.add(zona);
  }

  private renderBotonVolver(width: number, height: number): void {
    const ancho = 260;
    const alto = 60;
    const y = height - 50;

    const boton = this.add.graphics();
    const dibujar = (color: number) => {
      boton.clear();
      boton.fillStyle(color, 1);
      boton.fillRoundedRect(-ancho / 2, -alto / 2, ancho, alto, 14);
    };
    dibujar(COLOR_BOTON);
    boton.setPosition(width / 2, y);
    this.uiContainer.add(boton);

    this.uiContainer.add(
      this.add
        .text(width / 2, y, BOTON_VOLVER_BARRIO, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '20px',
          color: COLOR_TEXTO,
        })
        .setOrigin(0.5),
    );

    const zona = this.add.zone(width / 2, y, ancho, alto).setInteractive({ useHandCursor: true });
    zona.on('pointerover', () => dibujar(COLOR_BOTON_HOVER));
    zona.on('pointerout', () => dibujar(COLOR_BOTON));
    zona.on('pointerdown', () => dibujar(COLOR_BOTON_HOVER));
    zona.on('pointerup', () => {
      goTo(this, SCENE_KEYS.HUB);
    });
    this.uiContainer.add(zona);
  }
}
